import { Typography, type TypographyProps } from "@mui/material";
import { getTranslation } from "common";
import { useTranslations } from "hooks";
import {
  SLA_ACTIVITIES_TEXT_KEYS,
  slaActivityWorkingDuration,
  slaHealthSeverity,
} from "model/slaActivitiesModel";
import { SLA_EDITOR_TEXT_KEYS } from "model/slaEditorModel";
import { SLA_MONITOR_TEXT_KEYS } from "model/slaMonitorModel";
import React from "react";
import { useSlaActivities, type SlaActivityInstanceView } from "./slaActivitiesContext";

/**
 * **Riga riassuntiva di un'istanza SLA**: "<Target>: <salute> · <residuo> ·
 * <scadenza>". Serve dove un task ha **due** SLA vivi (attivita' + processo):
 * la vista principale resta quella di attivita' (e' la promessa che
 * l'assegnatario deve rispettare) e quella di processo compare come riga
 * secondaria, cosi' non sparisce.
 *
 * Testi solo dal catalogo: target (`sla.editor.target.*`), salute
 * (`sla.monitor.health.*`), ritardo (`sla.monitor.column.overdue`), durata
 * (`generic.*.caption`). Nessuna chiave nuova.
 */

export type SlaSummaryTarget = "activity" | "process";

const TARGET_KEYS: Record<SlaSummaryTarget, string> = {
  activity: SLA_EDITOR_TEXT_KEYS.targetActivity,
  process: SLA_EDITOR_TEXT_KEYS.targetProcess,
};

/** true quando lo SLA di processo e' messo peggio di quello di attivita'. */
export const slaProcessIsWorse = (
  activity: SlaActivityInstanceView,
  process: SlaActivityInstanceView | null,
): boolean =>
  process !== null &&
  slaHealthSeverity(process.detail.health) > slaHealthSeverity(activity.detail.health);

/** Testo della riga riassuntiva (anche per tooltip e nomi accessibili). */
export const useSlaInstanceSummary = (
  view: SlaActivityInstanceView | null,
  target: SlaSummaryTarget,
): string | null => {
  const translations = useTranslations();
  const context = useSlaActivities();
  if (!view) return null;
  const { detail, countdown } = view;
  const locale = context?.locale || "en";
  const overdueSeconds = countdown.overdueSeconds ?? 0;
  const isOverdue = overdueSeconds > 0;
  const duration = slaActivityWorkingDuration(
    isOverdue ? overdueSeconds : countdown.remainingSeconds,
    locale,
  );
  const durationText = duration
    ? `${duration.value} ${getTranslation(translations, duration.unitKey)}`
    : getTranslation(translations, SLA_ACTIVITIES_TEXT_KEYS.countdownNotAvailable);
  const remainingText =
    isOverdue && duration
      ? `${getTranslation(translations, SLA_MONITOR_TEXT_KEYS.columnOverdue)} +${durationText}`
      : durationText;
  const parts = [
    detail.healthKey ? getTranslation(translations, detail.healthKey) : null,
    remainingText,
    detail.effectiveLabel || null,
  ].filter(Boolean);
  return `${getTranslation(translations, TARGET_KEYS[target])}: ${parts.join(" · ")}`;
};

interface SlaInstanceSummaryLineProps extends Omit<TypographyProps, "children"> {
  view: SlaActivityInstanceView | null;
  target: SlaSummaryTarget;
  testId?: string;
}

/** Riga riassuntiva resa come caption (dettaglio, badge esteso, banner). */
const SlaInstanceSummaryLine: React.FC<SlaInstanceSummaryLineProps> = ({
  view,
  target,
  testId,
  ...typography
}) => {
  const text = useSlaInstanceSummary(view, target);
  if (!view || !text) return null;
  return (
    <Typography
      variant="caption"
      component="div"
      color="text.secondary"
      data-testid={testId}
      data-instance-id={view.instance.id}
      data-health={view.detail.health === null ? "" : view.detail.health}
      data-working-seconds={view.countdown.remainingSeconds ?? ""}
      data-overdue-seconds={view.countdown.overdueSeconds ?? ""}
      aria-live="off"
      {...typography}
    >
      {text}
    </Typography>
  );
};

export default SlaInstanceSummaryLine;
